import React, { useRef } from 'react';
import { motion, useAnimationControls, useInView, useMotionValue } from 'framer-motion';

interface Testimonial {
  name: string;
  role: string;
  company?: string;
  image: string;
  quote: string;
}

interface StudentTestimonialsProps {
  testimonials: Testimonial[];
}

const StudentTestimonials: React.FC<StudentTestimonialsProps> = ({ testimonials }) => {
  const controls = useAnimationControls();
  const containerRef = useRef(null);
  const isInView = useInView(containerRef);
  const x = useMotionValue(0);

  const startAnimation = () => {
    if (isInView) {
      controls.start({
        x: [0, -2000],
        transition: {
          duration: 40,
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop",
        },
      });
    }
  };

  const handleHoverStart = () => {
    controls.stop();
  };

  const handleHoverEnd = () => {
    controls.start({
      x: [x.get(), -2000],
      transition: {
        duration: 40,
        ease: "linear",
        repeat: Infinity,
        repeatType: "loop",
      },
    });
  };

  React.useEffect(() => {
    startAnimation();
  }, [isInView]);

  return (
    <section className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-[#15213A] mb-4">
            What Our Students Say
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Join many aspiring and like-minded engineers who are looking to transform their career through cloud engineering. Be part of a growing community dedicated to success.
          </p>
        </div>

        <div 
          ref={containerRef}
          className="relative w-full overflow-hidden mb-12"
          style={{ height: '400px' }}
        >
          <motion.div
            className="flex gap-8 absolute"
            style={{ x }}
            animate={controls}
            onHoverStart={handleHoverStart}
            onHoverEnd={handleHoverEnd}
          >
            {[...testimonials, ...testimonials, ...testimonials].map((testimonial, index) => (
              <motion.div
                key={`${testimonial.name}-${index}`}
                className="bg-white p-8 rounded-2xl shadow-lg w-[400px] flex-shrink-0"
              >
                <div className="flex items-center mb-6">
                  {testimonial.image.includes('ui-avatars.com') ? (
                    <div className="w-12 h-12 rounded-full bg-[#1C98ED] flex items-center justify-center text-white text-xl font-semibold">
                      {testimonial.name.charAt(0)}
                    </div>
                  ) : (
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  )}
                  <div className="ml-4">
                    <h3 className="font-semibold text-[#15213A]">{testimonial.name}</h3>
                    <p className="text-sm text-gray-600">
                      {testimonial.role}
                      {testimonial.company && ` at ${testimonial.company}`}
                    </p>
                  </div>
                </div>
                <div className="italic text-gray-600">{testimonial.quote}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Star Rating */}
        <div className="text-center">
          <div className="flex justify-center gap-1 mb-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <svg
                key={star}
                className="w-6 h-6 text-yellow-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <p className="text-lg font-medium text-gray-700">Average rating of 4.9/5</p>
        </div>
      </div>
    </section>
  );
};

export default StudentTestimonials; 